import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkshopRecurringRules.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const emptyRule = {
  location_id: '',
  day_of_week: 0,
  start_time: '10:00',
  end_time: '11:30',
  workshop_type_id: '',
  capacity_override: '',
  effective_from: '',
  effective_until: '',
  is_active: true
};

function WorkshopRecurringRules() {
  const [rules, setRules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({ ...emptyRule });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rulesRes, locationsRes, typesRes] = await Promise.all([
        axios.get('/api/workshops/recurring-rules'),
        axios.get('/api/workshops/locations'),
        axios.get('/api/workshops/types')
      ]);
      setRules(rulesRes.data || []);
      setLocations(locationsRes.data || []);
      setTypes(typesRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load recurring rules');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingRule('new');
    setFormData({ ...emptyRule });
  };

  const handleEdit = (rule) => {
    setEditingRule(rule.id);
    setFormData({
      ...rule,
      capacity_override: rule.capacity_override || ''
    });
  };

  const handleCancel = () => {
    setEditingRule(null);
    setFormData({ ...emptyRule });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        day_of_week: parseInt(formData.day_of_week),
        capacity_override: formData.capacity_override ? parseInt(formData.capacity_override) : null
      };

      if (editingRule === 'new') {
        await axios.post('/api/workshops/recurring-rules', payload);
      } else {
        await axios.put(`/api/workshops/recurring-rules/${editingRule}`, payload);
      }
      setEditingRule(null);
      setFormData({ ...emptyRule });
      await fetchAll();
    } catch (err) {
      console.error('Failed to save rule:', err);
      alert('Failed to save recurring rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this recurring rule?')) return;
    try {
      await axios.delete(`/api/workshops/recurring-rules/${ruleId}`);
      await fetchAll();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      alert('Failed to delete rule');
    }
  };

  const handleGenerateSessions = async () => {
    if (!window.confirm('Generate sessions from all active recurring rules for the next 30 days?')) return;
    try {
      setGenerating(true);
      const response = await axios.post('/api/workshops/sessions/generate');
      setGenerateResult(response.data);
    } catch (err) {
      console.error('Failed to generate sessions:', err);
      alert('Failed to generate sessions');
    } finally {
      setGenerating(false);
    }
  };

  const getLocationName = (locationId) => {
    const loc = locations.find((l) => l.id === locationId);
    return loc ? loc.name : 'Unknown';
  };

  const getTypeName = (typeId) => {
    const type = types.find((t) => t.id === typeId);
    return type ? type.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="workshop-recurring-rules">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading recurring rules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-recurring-rules">
      <div className="ws-page-header">
        <h1>Recurring Schedules</h1>
        <div className="ws-header-actions">
          <button
            className="ws-btn-secondary"
            onClick={handleGenerateSessions}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Sessions'}
          </button>
          <button className="ws-btn-primary" onClick={handleNew}>
            Add Rule
          </button>
        </div>
      </div>

      {error && <div className="ws-error">{error}</div>}

      {generateResult && (
        <div className="ws-generate-result">
          <p>Generated {generateResult.created || 0} sessions. {generateResult.skipped || 0} skipped (already exist).</p>
          <button className="ws-btn-secondary ws-btn-sm" onClick={() => setGenerateResult(null)}>
            Dismiss
          </button>
        </div>
      )}

      {editingRule && (
        <div className="ws-section ws-edit-form">
          <h2>{editingRule === 'new' ? 'New Recurring Rule' : 'Edit Recurring Rule'}</h2>

          <div className="ws-form-grid">
            <div className="ws-form-group">
              <label>Location</label>
              <select
                value={formData.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
            <div className="ws-form-group">
              <label>Day of Week</label>
              <select
                value={formData.day_of_week}
                onChange={(e) => handleChange('day_of_week', e.target.value)}
              >
                {DAYS_OF_WEEK.map((day, index) => (
                  <option key={index} value={index}>{day}</option>
                ))}
              </select>
            </div>
            <div className="ws-form-group">
              <label>Start Time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>Workshop Type</label>
              <select
                value={formData.workshop_type_id}
                onChange={(e) => handleChange('workshop_type_id', e.target.value)}
              >
                <option value="">Select Type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="ws-form-group">
              <label>Capacity Override</label>
              <input
                type="number"
                value={formData.capacity_override}
                onChange={(e) => handleChange('capacity_override', e.target.value)}
                placeholder="Leave empty for default"
                min="1"
              />
            </div>
            <div className="ws-form-group">
              <label>Effective From</label>
              <input
                type="date"
                value={formData.effective_from || ''}
                onChange={(e) => handleChange('effective_from', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>Effective Until</label>
              <input
                type="date"
                value={formData.effective_until || ''}
                onChange={(e) => handleChange('effective_until', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>Active</label>
              <select
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => handleChange('is_active', e.target.value === 'true')}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="ws-form-actions">
            <button className="ws-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Rule'}
            </button>
            <button className="ws-btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="ws-section">
        <h2>Recurring Rules</h2>
        {rules.length === 0 ? (
          <div className="ws-empty">No recurring rules configured</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Day</th>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="ws-text-bold">{rule.location_name || getLocationName(rule.location_id)}</td>
                    <td>{DAYS_OF_WEEK[rule.day_of_week] || rule.day_of_week}</td>
                    <td>{rule.start_time} - {rule.end_time}</td>
                    <td>{rule.workshop_type_name || getTypeName(rule.workshop_type_id)}</td>
                    <td>{rule.capacity_override || 'Default'}</td>
                    <td>
                      <span className={`ws-badge ${rule.is_active ? 'ws-badge-confirmed' : 'ws-badge-cancelled'}`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="ws-action-btns">
                        <button className="ws-btn-secondary ws-btn-sm" onClick={() => handleEdit(rule)}>
                          Edit
                        </button>
                        <button className="ws-btn-danger ws-btn-sm" onClick={() => handleDelete(rule.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopRecurringRules;
